import { useState } from 'react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { formatCurrency, formatDate } from '~/utils/paymentUtils';
import { PayInvoiceDialog } from './PayInvoiceDialog';

export interface InvoiceLineData {
  lineItemId: string;
  description: string;
  amountCents: number;
  lineType: string;
  person?: { firstName?: string | null } | null;
}

export interface InvoiceData {
  invoiceId: string;
  key?: string | null;
  displayStatus: string;
  amountCents: number;
  dueDate?: string | null;
  postedAt?: string | null;
  memo?: string | null;
  autopayStatus: string;
  autopayFailureReason?: string | null;
  automaticPaymentAuthorized: boolean;
  automaticPaymentAuthorizationSource?: string | null;
  stripeCollectionMethod?: string | null;
  stripeHostedInvoiceUrl?: string | null;
  payableWithSavedMethod: boolean;
  lineItems?: InvoiceLineData[] | null;
}

interface ParentInvoicesListProps {
  invoices: InvoiceData[];
  familyEnrollmentId?: string | null;
  onPaymentStarted?: () => void;
}

function titleize(value?: string | null): string {
  if (!value) return '';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Ruling 24: the server no longer says a bare "Scheduled". A scheduled invoice
// arrives as "Charges Oct 1", "Sending Sep 20" or "Due Oct 15" — it names what
// happens and when — so the badge is keyed on that leading verb rather than on
// one exact word. "Paid", "Unpaid", "Payment Failed" and "Void" are unchanged.
const PENDING_STATUS_PREFIXES = ['Charges', 'Sending', 'Due', 'Scheduled'];

// Nothing is expected of a family for an invoice that is already settled.
const SETTLED_STATUSES = ['Paid', 'Void'];

export function statusVariant(displayStatus: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (displayStatus === 'Paid') return 'default';
  if (displayStatus === 'Payment Failed') return 'destructive';
  if (displayStatus === 'Payment Processing') return 'secondary';
  if (PENDING_STATUS_PREFIXES.some((prefix) => displayStatus.startsWith(prefix))) return 'secondary';
  return 'outline';
}

// Finding 7: "Collection: emailed invoice" and "charge_automatically" are our
// words, not a family's. A parent is told one of two things — we will charge
// them, or they owe us by a date.
function manualLabel(inv: InvoiceData): { text: string; tone: 'ok' | 'warn' | 'muted' } {
  if (SETTLED_STATUSES.includes(inv.displayStatus)) return { text: '', tone: 'muted' };
  if (inv.dueDate) return { text: `Pay by ${formatDate(inv.dueDate)}`, tone: 'muted' };

  return { text: 'Pay when you are ready', tone: 'muted' };
}

// Whether we will charge the family off-session is recorded on the invoice
// (Decision 3), so the label reads that flag rather than inferring intent from a
// subscription link or an autopay_status stamp. The in-flight states still
// describe what is happening.
export function autopayLabel(inv: InvoiceData): { text: string; tone: 'ok' | 'warn' | 'muted' } {
  // A send_invoice invoice is collected manually no matter what autopay_status the
  // writer stamped on it, so it is never described as automatic to the parent.
  if (inv.stripeCollectionMethod === 'send_invoice') return manualLabel(inv);

  switch (inv.autopayStatus) {
    case 'autopay_failed':
      return { text: `Auto-pay failed${inv.autopayFailureReason ? `: ${inv.autopayFailureReason}` : ''}`, tone: 'warn' };
    case 'autopay_processing':
      return { text: 'Auto-pay in progress', tone: 'ok' };
    case 'autopay_succeeded':
      return { text: 'Paid by auto-pay', tone: 'ok' };
    default:
      return inv.automaticPaymentAuthorized ? { text: 'Auto-pay', tone: 'ok' } : manualLabel(inv);
  }
}

const toneClass = { ok: 'text-foreground', warn: 'text-destructive', muted: 'text-muted-foreground' } as const;

// "Pay" is offered exactly when the backend says the invoice can be paid with a
// saved method (ruling 32): still owed, collected manually, and already in
// Stripe. A draft the parent pays finalizes at pay time, so it has no hosted
// page yet — which is why the hosted URL is no longer what "Pay" keys on. The
// hosted page survives only as the receipt on a paid invoice.
export function canPay(inv: InvoiceData): boolean {
  return Boolean(inv.payableWithSavedMethod);
}

export function hasReceipt(inv: InvoiceData): boolean {
  return inv.displayStatus === 'Paid' && Boolean(inv.stripeHostedInvoiceUrl);
}

// Below Tailwind's `md` breakpoint the table's right-hand columns, Amount and
// Pay among them, scroll off screen with nothing to say they are there, so a
// phone gets each invoice as its own block instead.
export const PHONE_MEDIA_QUERY = '(max-width: 767px)';

function invoiceName(inv: InvoiceData): string {
  return inv.memo || titleize(inv.key) || 'Invoice';
}

function ReceiptLink({ inv }: { inv: InvoiceData }) {
  return (
    <Button asChild variant="ghost" size="sm">
      <a href={inv.stripeHostedInvoiceUrl!} target="_blank" rel="noopener noreferrer">Receipt</a>
    </Button>
  );
}

export function ParentInvoicesList({ invoices, familyEnrollmentId, onPaymentStarted }: ParentInvoicesListProps) {
  const [payingInvoice, setPayingInvoice] = useState<InvoiceData | null>(null);
  const onPhone = useMediaQuery(PHONE_MEDIA_QUERY);

  return (
    <Card>
      <CardHeader><CardTitle>Invoices</CardTitle></CardHeader>
      <CardContent>
        {invoices.length === 0 ? (
          <p className="text-muted-foreground">No invoices for this school year yet.</p>
        ) : onPhone ? (
          <ul className="divide-y">
            {invoices.map((inv) => {
              const autopay = autopayLabel(inv);
              return (
                <li key={inv.invoiceId} className="space-y-3 py-4 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-4">
                    <span className="font-medium">{invoiceName(inv)}</span>
                    <span className="tabular-nums">{formatCurrency(inv.amountCents)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Badge variant={statusVariant(inv.displayStatus)}>{inv.displayStatus}</Badge>
                    {autopay.text ? (
                      <span className={`text-sm ${toneClass[autopay.tone]}`}>{autopay.text}</span>
                    ) : inv.dueDate ? (
                      <span className="text-sm text-muted-foreground">Due {formatDate(inv.dueDate)}</span>
                    ) : null}
                  </div>
                  {familyEnrollmentId && canPay(inv) && (
                    <Button className="w-full" onClick={() => setPayingInvoice(inv)}>Pay</Button>
                  )}
                  {hasReceipt(inv) && <ReceiptLink inv={inv} />}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => {
                  const autopay = autopayLabel(inv);
                  return (
                    <TableRow key={inv.invoiceId}>
                      <TableCell>{invoiceName(inv)}</TableCell>
                      <TableCell><Badge variant={statusVariant(inv.displayStatus)}>{inv.displayStatus}</Badge></TableCell>
                      <TableCell>{inv.dueDate ? formatDate(inv.dueDate) : 'N/A'}</TableCell>
                      <TableCell>
                        <span className={`text-sm ${toneClass[autopay.tone]}`}>{autopay.text}</span>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(inv.amountCents)}</TableCell>
                      <TableCell className="text-right">
                        {familyEnrollmentId && canPay(inv) && (
                          <Button variant="outline" size="sm" onClick={() => setPayingInvoice(inv)}>Pay</Button>
                        )}
                        {hasReceipt(inv) && <ReceiptLink inv={inv} />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <PayInvoiceDialog
        invoice={payingInvoice}
        familyEnrollmentId={familyEnrollmentId}
        open={Boolean(payingInvoice)}
        onOpenChange={(open) => { if (!open) setPayingInvoice(null); }}
        onPaid={onPaymentStarted}
      />
    </Card>
  );
}
